import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { resetPassword } from '../../services/api';

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  text: '#1E293B',
  textMuted: '#64748B',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentSoft: '#EFF4FF',
  border: '#E2E8F0',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT    = "'DM Sans', sans-serif";

// Mirror the server-side passwordSchema so we can give immediate feedback
// rather than waiting for a 400. Server still validates authoritatively.
const PASSWORD_RULES = [
  { test: (v) => v.length >= 12,            label: 'At least 12 characters' },
  { test: (v) => /[A-Z]/.test(v),           label: 'One uppercase letter' },
  { test: (v) => /[a-z]/.test(v),           label: 'One lowercase letter' },
  { test: (v) => /[0-9]/.test(v),           label: 'One number' },
  { test: (v) => /[^A-Za-z0-9]/.test(v),    label: 'One special character' },
];

function friendlyError(err) {
  const status = err?.response?.status;
  const serverMsg = err?.response?.data?.error;
  if (status === 400) return serverMsg || 'This reset link is no longer valid.';
  if (status === 429) return 'Too many attempts. Please wait a few minutes.';
  return 'Something went wrong. Please try again in a moment.';
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword]               = useState('');
  const [confirm, setConfirm]                 = useState('');
  const [submitting, setSubmitting]           = useState(false);
  const [error, setError]                     = useState('');

  const ruleStates = useMemo(
    () => PASSWORD_RULES.map((r) => ({ label: r.label, ok: r.test(password) })),
    [password],
  );
  const allRulesOk = ruleStates.every((r) => r.ok);
  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = !!token && allRulesOk && passwordsMatch && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await resetPassword(token, password);
      navigate('/parent/login', {
        replace: true,
        state: { error: 'Password updated. Please sign in with your new password.' },
      });
    } catch (err) {
      setError(friendlyError(err));
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <CenterCard>
        <h1 style={titleStyle}>Reset link is missing</h1>
        <p style={{ margin: '8px 0 18px', color: C.textMuted, fontSize: 15, lineHeight: 1.55 }}>
          This link is incomplete. Please use the button in your password reset email, or request a new one.
        </p>
        <Link to="/parent/forgot-password" style={linkStyle}>Request a new reset link</Link>
      </CenterCard>
    );
  }

  return (
    <CenterCard>
      <h1 style={titleStyle}>Choose a new password</h1>
      <p style={{ margin: '8px 0 22px', color: C.textMuted, fontSize: 15, lineHeight: 1.55 }}>
        Pick something strong — you'll use it next time you sign in to Lantern.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <Field
          id="password"
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(v) => { setPassword(v); setError(''); }}
          disabled={submitting}
        />

        <ul style={{
          margin: '8px 0 18px',
          padding: 0,
          listStyle: 'none',
          fontSize: 13,
          color: C.textMuted,
        }}>
          {ruleStates.map((r) => (
            <li key={r.label} style={{ marginTop: 4 }}>
              <span aria-hidden="true" style={{
                display: 'inline-block',
                width: 14,
                marginRight: 6,
                color: r.ok ? '#059669' : C.textMuted,
                fontWeight: 700,
              }}>{r.ok ? '✓' : '·'}</span>
              {r.label}
            </li>
          ))}
        </ul>

        <Field
          id="confirm"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(v) => { setConfirm(v); setError(''); }}
          disabled={submitting}
        />
        {confirm.length > 0 && !passwordsMatch && (
          <p style={{ margin: '6px 2px 0', fontSize: 13, color: C.redText }}>
            Passwords don't match yet.
          </p>
        )}

        {error && (
          <div role="alert" style={{
            marginTop: 14,
            padding: '10px 14px',
            background: C.redSoft,
            border: `1px solid ${C.redBorder}`,
            borderRadius: 10,
            color: C.redText,
            fontSize: 14,
          }}>{error}</div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            marginTop: 18,
            width: '100%',
            padding: '12px 18px',
            background: !canSubmit ? '#94A3B8' : C.accent,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontFamily: HEADING_FONT,
            fontSize: 15,
            fontWeight: 700,
            cursor: !canSubmit ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>

      <div style={{ marginTop: 22, fontSize: 14 }}>
        <Link to="/parent/login" style={linkStyle}>← Back to sign in</Link>
      </div>
    </CenterCard>
  );
}

// ─── Tiny presentational helpers ──────────────────────────────────────────────

function CenterCard({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: C.bg,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '64px 16px',
      fontFamily: BODY_FONT,
      color: C.text,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 440,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: '36px 32px 32px',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
      }}>{children}</div>
    </div>
  );
}

function Field({ id, label, type, value, onChange, autoComplete, disabled }) {
  return (
    <div style={{ marginTop: 14 }}>
      <label htmlFor={id} style={{
        display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6,
      }}>{label}</label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required
        style={{
          width: '100%',
          padding: '12px 14px',
          fontFamily: BODY_FONT,
          fontSize: 15,
          color: C.text,
          background: C.surface,
          border: `1.5px solid ${C.border}`,
          borderRadius: 10,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

const titleStyle = {
  margin: 0,
  fontFamily: HEADING_FONT,
  fontSize: 26,
  fontWeight: 700,
  letterSpacing: '-0.01em',
};

const linkStyle = {
  color: C.accent, textDecoration: 'none', fontWeight: 600,
};
